// src/widgets/ConcurrentWidget.jsx
// Fetch data via GET /dashboard/overview?widget=concurrent
//
// Kontrak backend BARU (perlu disesuaikan di dashboard_api.rb — histogram,
// mengikuti pola yang sama dengan 'traffic_chart'): response BODY = ARRAY MENTAH.
//
// Mode "total" (filter.group_by kosong):
//   [{ bucket, active_users, active_sessions }, ...]
//
// Mode "grouped" (filter.group_by = realm|nasipaddress|server_name):
//   Nama kolom group SAMA PERSIS dengan filter.group_by, contoh group_by=realm:
//   [{ bucket, realm, active_users, active_sessions }, ...]
//   Backend hanya menerapkan top-N (filter.limit) saat group_by berisi TEPAT 1 kolom.
//   Kita tetap slice top-N di client sebagai pengaman tambahan (default limit 5).
import { useMemo } from 'react';
import { Wifi } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend,
  ResponsiveContainer, CartesianGrid,
} from 'recharts';
import Empty from './Empty';
import useWidgetData from '../hooks/useWidgetData';
import { fmtBucket, fillBucketGaps } from '../utils/chartFormat';

const COLORS = ['var(--accent-green)', 'var(--accent-blue)', 'var(--accent-orange)', 'var(--accent-cyan)', 'var(--accent-purple, #a78bfa)'];

const GROUP_BY_LABELS = {
  username:     'Username',
  nasipaddress: 'NAS Device',
  realm:        'Realm',
  server_name:  'Server',
  groupname:    'Profile',
};

// Grafik memakai `active_sessions` per bucket (bukan `active_users`); nilainya string
// ("1"). Nama lama `online_count`/`count` tetap didukung sebagai fallback.
const countOf = (row) => Number(row.active_sessions ?? row.online_count ?? row.count ?? 0) || 0;

export default function ConcurrentWidget({ filter = {} }) {
  const groupBy = filter.group_by || '';
  const limit   = filter.limit || 5;
  const { data, loading } = useWidgetData('concurrent', { ...filter, limit });

  // Sama seperti traffic_chart: backend mengembalikan array mentah.
  const rawItems = Array.isArray(data) ? data : [];

  // ── Mode total: pakai langsung ──
  // ── Mode grouped: pivot rows -> { bucket, [group]: count } + top-N group ──
  const { chartData, groups } = useMemo(() => {
    if (!groupBy) {
      const byBucket = new Map(rawItems.map(row => [String(row.bucket), countOf(row)]));
      // Bucket tanpa sesi tidak dikirim backend → tampilkan sebagai 0.
      const normalized = fillBucketGaps([...byBucket.keys()]).map(b => ({ bucket: b, online: byBucket.get(b) || 0 }));
      return { chartData: normalized, groups: [] };
    }

    const bucketMap = {};
    const totals = {};

    rawItems.forEach((row) => {
      const g     = row[groupBy] ?? row.group ?? row.label ?? 'lainnya';
      const count = countOf(row);

      if (!bucketMap[row.bucket]) bucketMap[row.bucket] = { bucket: row.bucket };
      bucketMap[row.bucket][g] = count;
      totals[g] = (totals[g] || 0) + count;
    });

    const topGroups = Object.entries(totals)
      .sort((a, b) => b[1] - a[1])
      .slice(0, limit)
      .map(([g]) => g);

    // Isi bucket kosong dengan 0 untuk semua group teratas.
    const sorted = fillBucketGaps(Object.keys(bucketMap)).map(b => {
      const row = bucketMap[b] || { bucket: b };
      topGroups.forEach(g => { row[g] = row[g] || 0; });
      return row;
    });
    return { chartData: sorted, groups: topGroups };
  }, [rawItems, groupBy, limit]);

  const isEmpty = groupBy ? groups.length === 0 : chartData.length === 0;

  return (
    <div style={{
      padding: 16,
      height: '100%',
      boxSizing: 'border-box',
      background: 'var(--bg-card)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius-lg)',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
    }}>
      <div style={{
        fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)',
        marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0,
      }}>
        <Wifi size={13} color="var(--accent-green)" />
        Concurrent Users {groupBy ? `per ${GROUP_BY_LABELS[groupBy] || groupBy} (Top ${limit})` : ''}
      </div>

      <div style={{ flex: 1, minHeight: 0 }}>
        {loading
          ? <div className="skeleton" style={{ height: '100%' }} />
          : isEmpty
            ? <Empty />
            : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 4, right: 8, bottom: 0, left: -20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="bucket" tickFormatter={fmtBucket}
                    tick={{ fontSize: 10, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false}
                    tick={{ fontSize: 10, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} width={30} />
                  <Tooltip
                    formatter={(v, n) => [v, n]}
                    labelFormatter={fmtBucket}
                    contentStyle={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 11 }}
                  />
                  {groupBy && <Legend wrapperStyle={{ fontSize: 10 }} />}
                  {groupBy
                    ? groups.map((g, i) => (
                        <Bar key={g} dataKey={g} name={g} fill={COLORS[i % COLORS.length]} radius={[3, 3, 0, 0]} />
                      ))
                    : <Bar dataKey="online" name="Sesi aktif" fill="var(--accent-green)" radius={[3, 3, 0, 0]} />
                  }
                </BarChart>
              </ResponsiveContainer>
            )
        }
      </div>
    </div>
  );
}